import { Formik } from 'formik';
import {
	CerebroDrawer,
	CerebroHeading,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
} from '../../shared/components';
import { CompanyNda } from '../../shared/models';
import * as Yup from 'yup';
import { DateUtils, FormikUtils } from '../../shared/utils';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { useNavigateToNextPage } from '../../shared/hooks';
import { useMutation } from 'react-query';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { getApiResponseData } from '../../shared/utils/axios';

interface AssociateCompanyNdaProps {
	open: boolean;
	dealGuid: string | undefined;
	availableNdas: CompanyNda[];
	onClose: () => void;
}

export const AssociateCompanyNda = ({ open, dealGuid, availableNdas, onClose }: AssociateCompanyNdaProps) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const navigateToNextPage = useNavigateToNextPage();

	const ndaMutation = useMutation(
		async ({ values }: AssociateNdaValuesMutationVariables) => {
			const { data, error } = await getApiResponseData<CompanyNda>(
				api.patch(CerebroApiEndpoints.Deals.AssociateNda(dealGuid, values.ndaGuid))
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				onClose();
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={onClose}>
			<Formik
				enableReinitialize
				validationSchema={AssociateSchema}
				onSubmit={FormikUtils.MutateOnSubmit(ndaMutation.mutate)}
				initialValues={{
					ndaGuid: '',
				}}
			>
				{({ isSubmitting, handleSubmit, setFieldValue, values, touched, errors }) => (
					<form onSubmit={handleSubmit}>
						<CerebroHeading size="h2" component="h3" gutterBottom>
							Assoicate Mutually Exclusive NDA
						</CerebroHeading>

						<Table>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Effective Date</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>Expiration Date</TableCell>
									<TableCell sx={{ fontWeight: 'bold' }}>&nbsp;</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{availableNdas.map(nda => (
									<TableRow key={nda.guid}>
										<TableCell>{nda.companyName}</TableCell>
										<TableCell>
											{DateUtils.FormatStringAsShortDate(nda.effectiveDate?.toString()) ?? '--'}
										</TableCell>
										<TableCell>
											{DateUtils.FormatStringAsShortDate(nda.expirationDate?.toString()) ?? '--'}
										</TableCell>
										<TableCell>
											<CerebroSecondaryButton
												disabled={values.ndaGuid === nda.guid || isSubmitting}
												onClick={() => setFieldValue('ndaGuid', nda.guid)}
											>
												{values.ndaGuid === nda.guid ? 'Selected' : 'Select'}
											</CerebroSecondaryButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
						{FormikUtils.IsInvalid('ndaGuid', errors, touched) && (
							<CerebroText variant="error">{errors.ndaGuid}</CerebroText>
						)}

						<Grid container sx={{ marginY: 3 }}>
							<Grid item>
								<CerebroPrimaryButton loading={isSubmitting} type="submit">
									Associate &amp; Execute NDA
								</CerebroPrimaryButton>
							</Grid>
						</Grid>
					</form>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type AssociateNdaValuesMutationVariables = FormikUtils.MutateOnSubmitVariables<AssociateNdaValues>;
interface AssociateNdaValues {
	ndaGuid: string;
}

const AssociateSchema = Yup.object().shape({
	ndaGuid: Yup.string().required('Must select a NDA.'),
});
